import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { iaRa } from 'lib/helpers/utility';

import AgTablePage from 'components/shared/AgTablePage/AgTablePage';

import { getDefaultColumns } from './lib/columns';

const ROW_HEIGHT = 40;
const HEADER_HEIGHT = 80;
const EMPTY_STATE_HEIGHT = 280;
const MAX_HEIGHT = 786;

const STATES_FILTERS = {
  search: {
    type: 'search',
    fields: [{ field: 'name', label: 'Name' }]
  }
};

const StatesPickerModal = forwardRef(
  ({ providerId, initialStateIds, stateOptions, blockedStateIds, isInNetwork, onSave }, ref) => {
    const initialSelectedIds = useMemo(() => iaRa(initialStateIds), [initialStateIds]);
    const blockedIds = useMemo(() => new Set(iaRa(blockedStateIds)), [blockedStateIds]);

    const oppositeLabel = isInNetwork ? 'out-of-network' : 'in-network';

    const states = useMemo(
      () =>
        iaRa(stateOptions).map((opt) => ({
          id: opt.value,
          name: opt.label,
          abbreviation: opt.abbreviation,
          isBlocked: blockedIds.has(opt.value)
        })),
      [stateOptions, blockedIds]
    );

    const initialSelectedRows = useMemo(
      () => states.filter((row) => initialSelectedIds.includes(row.id) && !row.isBlocked),
      [states, initialSelectedIds]
    );

    const [selectedRows, setSelectedRows] = useState(initialSelectedRows);
    const hasInitializedSelectionRef = useRef(false);

    const defaultColumns = useMemo(() => getDefaultColumns({ oppositeLabel }), [oppositeLabel]);

    const getStatesTableData = useCallback(
      ({ params }) => {
        let filtered = states;

        const searchTerm = params?.filters?.search?.searchTerm?.trim().toLowerCase();
        if (searchTerm) {
          const enabledFields = iaRa(params.filters.search.fields);
          const fields = enabledFields.length ? enabledFields : ['name', 'abbreviation'];
          filtered = filtered.filter((row) =>
            fields.some((field) =>
              String(row?.[field] ?? '')
                .toLowerCase()
                .includes(searchTerm)
            )
          );
        }

        return { data: { states: filtered, count: filtered.length } };
      },
      [states]
    );

    const handleSelect = useCallback((nodes) => {
      setSelectedRows(
        iaRa(nodes)
          .map((node) => node?.data)
          .filter(Boolean)
      );
    }, []);

    const getRowId = useCallback((params) => params.data?.id, []);

    const isRowSelectable = useCallback((node) => !node.data?.isBlocked, []);

    const applyInitialSelection = useCallback(
      (params) => {
        if (hasInitializedSelectionRef.current) return;
        if (!initialSelectedIds.length) {
          hasInitializedSelectionRef.current = true;
          return;
        }
        if (!params.api.getDisplayedRowCount()) return;

        let selectedCount = 0;
        params.api.forEachNode((node) => {
          if (initialSelectedIds.includes(node.data?.id) && !node.data?.isBlocked) {
            node.setSelected(true);
            selectedCount += 1;
          }
        });

        if (selectedCount > 0) hasInitializedSelectionRef.current = true;
      },
      [initialSelectedIds]
    );

    useImperativeHandle(
      ref,
      () => ({
        handleSave: () => {
          const nextIds = selectedRows.map((row) => row?.id).filter((id) => id != null);
          onSave?.(providerId, nextIds);
        }
      }),
      [onSave, providerId, selectedRows]
    );

    const rowCount = states.length;
    const dynamicHeight = Math.min(
      rowCount === 0 ? HEADER_HEIGHT + EMPTY_STATE_HEIGHT : HEADER_HEIGHT + rowCount * ROW_HEIGHT,
      MAX_HEIGHT
    );

    return (
      <div className="flex flex-col" style={{ height: dynamicHeight }}>
        <AgTablePage
          noHeader
          noDisplayButton
          disableActions
          statusBar={null}
          pagination={false}
          name="provider-states"
          queryKey="states"
          useData={getStatesTableData}
          defaultColumns={defaultColumns}
          defaultFilters={STATES_FILTERS}
          onSelect={handleSelect}
          rowSelection="multiple"
          rowMultiSelectWithClick
          suppressRowClickSelection={false}
          getRowId={getRowId}
          isRowSelectable={isRowSelectable}
          onFirstDataRendered={applyInitialSelection}
          onRowDataUpdated={applyInitialSelection}
        />
      </div>
    );
  }
);

StatesPickerModal.displayName = 'StatesPickerModal';

export default StatesPickerModal;
